import { z } from 'zod';
import { isoDateTimeSchema } from './schemas.js';
/** Zeitpunkt, einheitlich als UTC-ISO gespeichert. */
const instant = isoDateTimeSchema.transform((value) => new Date(value).toISOString());
/**
 * Umfragen.
 *
 * Eine Umfrage gehoert zu einem Bereich (Mannschaft, Gruppe, Sektion ...) -
 * dort wird das Recht geprueft. Wer gefragt wird, steht beim Start fest:
 * die Mitglieder des Bereichs (optional nur bestimmte Funktionen) und/oder
 * einzeln ausgewaehlte Personen.
 */
export const POLL_QUESTION_KIND = ['SINGLE', 'MULTIPLE', 'TEXT'];
export const POLL_QUESTION_KIND_LABELS = {
    SINGLE: 'Eine Antwort',
    MULTIPLE: 'Mehrere Antworten',
    TEXT: 'Freitext',
};
export const POLL_AUDIENCE_MODE = ['UNIT', 'PERSONS'];
export const POLL_LIMITS = {
    questions: 20,
    options: 20,
    textAnswer: 2000,
    persons: 500,
};
const trimmed = (max) => z.string().trim().min(1, 'Pflichtfeld').max(max);
export const pollAudienceSchema = z.object({
    mode: z.enum(POLL_AUDIENCE_MODE),
    /** Untergeordnete Bereiche mitnehmen (nur bei UNIT). */
    includeChildren: z.boolean().default(true),
    /** Nur Personen mit diesen Funktionen - leer heisst alle Funktionen. */
    roleIds: z.array(z.string().min(1)).max(50).default([]),
    /** Spieler aus dem Kader der Mannschaften des Bereichs (aktuelle Saison). */
    includeRoster: z.boolean().default(true),
    /** Einzeln ausgewaehlte Personen - bei UNIT zusaetzlich. */
    personIds: z.array(z.string().min(1)).max(POLL_LIMITS.persons).default([]),
});
export const pollQuestionSchema = z
    .object({
    kind: z.enum(POLL_QUESTION_KIND),
    text: trimmed(300),
    required: z.boolean().default(true),
    options: z.array(trimmed(200)).max(POLL_LIMITS.options).default([]),
})
    .superRefine((question, ctx) => {
    if (question.kind === 'TEXT')
        return;
    if (question.options.length < 2) {
        ctx.addIssue({ code: 'custom', path: ['options'], message: 'Mindestens zwei Antwortmöglichkeiten' });
    }
    const unique = new Set(question.options.map((option) => option.toLowerCase()));
    if (unique.size !== question.options.length) {
        ctx.addIssue({ code: 'custom', path: ['options'], message: 'Antwortmöglichkeiten doppelt' });
    }
});
export const pollCreateSchema = z.object({
    organisationUnitId: z.string().min(1),
    title: trimmed(150),
    description: z.string().trim().max(2000).nullish().transform((value) => value || null),
    closesAt: instant.nullish().transform((value) => value ?? null),
    anonymous: z.boolean().default(false),
    /** Teilnehmende sehen nach ihrer Antwort das Gesamtergebnis. */
    showResults: z.boolean().default(true),
    audience: pollAudienceSchema,
    questions: z.array(pollQuestionSchema).min(1, 'Mindestens eine Frage').max(POLL_LIMITS.questions),
});
export const pollUpdateSchema = z.object({
    closesAt: instant.nullable().optional(),
    showResults: z.boolean().optional(),
});
export const pollAudiencePreviewSchema = z.object({
    organisationUnitId: z.string().min(1),
    audience: pollAudienceSchema,
});
export const pollAnswerSchema = z.object({
    answers: z
        .array(z.object({
        questionId: z.string().min(1),
        optionIds: z.array(z.string().min(1)).max(POLL_LIMITS.options).default([]),
        text: z.string().trim().max(POLL_LIMITS.textAnswer).nullish().transform((value) => value || null),
    }))
        .max(POLL_LIMITS.questions),
});
/** Ja/Nein/Vielleicht als Vorlage fuer eine Einzelauswahl. */
export const POLL_YES_NO_MAYBE = ['Ja', 'Nein', 'Vielleicht'];
/** Push-Anmeldung eines Browsers (PushSubscription.toJSON()). */
export const pushSubscriptionSchema = z.object({
    endpoint: z.string().url().max(1000),
    keys: z.object({
        p256dh: z.string().regex(/^[A-Za-z0-9_-]+=*$/).min(40).max(200),
        auth: z.string().regex(/^[A-Za-z0-9_-]+=*$/).min(8).max(100),
    }),
});
export const pushEndpointSchema = z.object({ endpoint: z.string().url().max(1000) });
//# sourceMappingURL=polls.js.map